export interface EditNavigationLocation { readonly pathname: string; readonly search: string; readonly hash: string; readonly key?: string }
export interface BlockedEditNavigation { readonly location: EditNavigationLocation; readonly proceed: () => void; readonly reset: () => void }
export const editNavigationTarget = (location: EditNavigationLocation) => location.pathname + location.search + location.hash;

/** Match the page's actual React key and editor-unmounting view, not its browsing query. */
export function editResourceKey(location: Pick<EditNavigationLocation, "pathname" | "search"> & Partial<Pick<EditNavigationLocation, "hash">>) {
  const query = new URLSearchParams(location.search);
  let resource: unknown[] = [];
  switch (location.pathname) {
    case "/ui-preview":
      // Only the full-page editor is unmounted when its preview category changes.
      resource = [location.hash === "#page-form" ? "page-form" : "preview"];
      break;
    case "/dataset-management":
      resource = [Number(query.get("projectId")), query.get("view") === "settings" ? "settings" : "datasets"];
      break;
    case "/annotation-data-management":
      resource = [query.get("projectId") ?? "preferred", query.get("datasetId") ?? "all"];
      break;
    case "/config-management":
      resource = [query.get("view") ?? "label", query.get("libraryId") ?? query.get("library") ?? "0"];
      break;
    case "/label-library-detail":
      resource = [query.get("libraryId") ?? "0"];
      break;
    case "/operation-console": {
      const submissionId = Number(query.get("submissionId") ?? 0);
      resource = [Number(query.get("taskId")), submissionId, query.get("mode") === "view" || submissionId > 0 ? "view" : "edit"];
      break;
    }
  }
  return JSON.stringify([location.pathname, ...resource]);
}

const sameAttempt = (initial: BlockedEditNavigation, current: BlockedEditNavigation | null) => Boolean(current &&
  current.location.key === initial.location.key && editNavigationTarget(current.location) === editNavigationTarget(initial.location));

/** Late confirmation results must not reset or proceed a newer router attempt. */
export async function protectBlockedEditNavigation(initial: BlockedEditNavigation, readCurrent: () => BlockedEditNavigation | null,
  request: (action: () => boolean, options: { navigationTarget: string }) => Promise<boolean>) {
  const accepted = await request(() => {
    const current = readCurrent();
    if (!sameAttempt(initial, current)) return false;
    current!.proceed();
    return true;
  }, { navigationTarget: editNavigationTarget(initial.location) });
  const current = readCurrent();
  if (!accepted && sameAttempt(initial, current)) current!.reset();
  return accepted;
}
